/**
 * Phase 6 cross-cutting PR 2 — notification allowlist + click-destination resolver.
 *
 * **Locked 5-key allowlist** (per `direction.md` §1 + `OVERRIDES.md`
 * D4). Vanilla has 9 `TYPE_CONFIG` keys; only these 5 are scope for
 * React. The other 4 (`plan_escalated`, `plan_ready`, `context_note`,
 * `meet_reminder`) are inventory-only — they do NOT render in the
 * bell dropdown and have no React click destination.
 *
 * **NU-1 (2026-06-09, user-approved exception):** the allowlist gains
 * exactly ONE sixth type, `plan_nudge` — plan-lifecycle reminders
 * (draft next week / log sessions / season wrap). Unlike the locked 5,
 * its click destination may come from the row's `metadata.href`,
 * validated through `sanitizeInternalHref` (strict internal-route
 * allowlist; anything else falls back to `/plan`). The locked 5 keep
 * their hardcoded routes and NEVER consult metadata.
 *
 * **Click destinations** were re-mapped from vanilla's hash routes to
 * React paths. The vanilla `roster` target has no athlete-side React
 * equivalent (deferred trainer surface), so `team_joined` re-routes
 * to `/v2/team`, and `ai_escalation` opens an in-place modal (deferred
 * to PR 2b — for now it falls back to the secondary CTA target of
 * `/v2/chat`, per `direction.md` §1).
 *
 * Sourced from:
 *   - `.claude/references/phase-6-cross-cutting-design/direction.md` §1
 *   - `.claude/references/phase-6-cross-cutting-design/OVERRIDES.md` D4
 */

import type { IconName } from '../components/icons/registry';

export const NOTIFICATION_ALLOWLIST = [
  'kudo_received',
  'plan_approved',
  'team_joined',
  'ai_escalation',
  'motivational_message',
  'plan_nudge',
  // CG-4 (founder-ratified N2): the bell is the guardian's urgency
  // channel — an `approval_request` row (written by the CG-3 backend
  // when a reflow acceptance is held) badges the bell and cold-opens
  // the /approvals inbox. Copy stays plain and non-medical (it names
  // the athlete and the action — never a signal or a health word).
  'approval_request',
  // Pass 3 (coach plan-review revival): the athlete-escalation row the
  // dormant backend has always written to trainers — now that the coach
  // tenant lives in /approvals, it earns its bell entry + cold-open.
  'plan_escalated',
  // F-514 — a mutual-confirmation invite (guardian↔athlete) is a CONSENT
  // decision waiting on the recipient; the server has always written the row
  // but the bell filtered it out, leaving (e.g.) a guardian with no visible
  // path to an athlete-initiated request (UAT-B2). Deep link: the row's
  // server-set metadata.href (sanitized), so each direction lands on ITS
  // accept surface — /guardian for the invited adult, /settings#guardians for
  // the invited athlete (the metadata-less fallback).
  'relationship_invite',
  // B195 (2026-09-16) — a guardian opened an under-13 deletion request; the
  // subject is told at request time and can cancel from Settings → Guardians
  // (the ratified cooling-off is "visible and cancellable throughout").
  'under13_deletion_requested',
  // B394 (2026-10-14) — the guardian-pays notices. The server (api/lib/billing/
  // guardianLicense.js) has always stored these rows and GET /api/notifications
  // has always returned them, but the bell drew none of them: a payer was never
  // shown that coverage started, stopped, failed to stop, was rejected, or ended
  // with the athlete's account. One destination, Settings → Subscription, where
  // the payer can act on each.
  // `family_plan_ask` is deliberately NOT here (coordinator ruling): Family is
  // archived and, with guardian_license_v1 off, the web Ask is hidden (B377 Q5),
  // so drawing it would send a guardian to a Subscription page where nothing can
  // be bought. Re-add it (list, meta, icon, click case) at the flip.
  'guardian_license_started',
  'guardian_license_stopped',
  'guardian_license_stop_failed',
  'guardian_license_rejected',
  'guardian_license_ended',
] as const;

export type NotificationType = (typeof NOTIFICATION_ALLOWLIST)[number];

/** Type-narrow helper: returns true if the type is one of the allowlisted keys. */
export function isAllowlistedNotificationType(type: string): type is NotificationType {
  return (NOTIFICATION_ALLOWLIST as readonly string[]).includes(type);
}

/**
 * Per-type emoji + warm-vs-signal tone classification.
 *
 * Tone vocabulary (per direction.md §1 PRESENTATION row):
 *   - `warm`   → kudo_received, motivational_message (today-soft glyph bg)
 *   - `ok`     → plan_approved
 *   - `signal` → team_joined
 *   - `danger` → ai_escalation (only place danger-coloured fill appears
 *                in the bell — and only on the glyph background, never
 *                on the row tint)
 */
export const NOTIFICATION_META: Record<
  NotificationType,
  { emoji: string; tone: 'warm' | 'ok' | 'signal' | 'danger' }
> = {
  kudo_received: { emoji: '🎉', tone: 'warm' },
  plan_approved: { emoji: '✅', tone: 'ok' },
  team_joined: { emoji: '👋', tone: 'signal' },
  ai_escalation: { emoji: '⚠️', tone: 'danger' },
  motivational_message: { emoji: '💪', tone: 'warm' },
  plan_nudge: { emoji: '🗓️', tone: 'signal' },
  // CG-4 — marine (signal) tone: an ask, never an alarm.
  approval_request: { emoji: '🔔', tone: 'signal' },
  // Pass 3 — same posture for the coach's review ask.
  plan_escalated: { emoji: '⏳', tone: 'signal' },
  // F-514 — marine (signal) tone: a connection ask, never an alarm.
  relationship_invite: { emoji: '🤝', tone: 'signal' },
  under13_deletion_requested: { emoji: '🔔', tone: 'signal' },
  // B394 — the guardian-pays notices: marine (signal) tone, never an alarm; the
  // one good-news row (coverage started) takes the ok tone.
  guardian_license_started: { emoji: '✅', tone: 'ok' },
  guardian_license_stopped: { emoji: '🔔', tone: 'signal' },
  guardian_license_stop_failed: { emoji: '🔔', tone: 'signal' },
  guardian_license_rejected: { emoji: '🔔', tone: 'signal' },
  guardian_license_ended: { emoji: '🔔', tone: 'signal' },
};

/**
 * CD-ICONS-01 — the DISPLAY-layer art for a notification row, behind
 * `icon_system_v1`. Read by `<NotifRow>`; nothing else consults it.
 *
 * ⚠️ A SEPARATE MAP, DELIBERATELY. `NOTIFICATION_META` above is a DATA contract
 * — its exact shape is asserted by `notifAllowlist.test.ts` with `toEqual`, and
 * adding a field to it broke two of those assertions. Loosening them to
 * `toMatchObject` would have weakened an existing test to fit a new one, which
 * is backwards. Keeping the art in its own map leaves the data contract
 * byte-identical and puts the icon where it belongs: the display layer. Same
 * layering as `KudoGlyph` over the frozen `kudoTypes.ts` (BUILD-NOTES S8).
 *
 * ✅ COMPLETE AS OF A2. This map used to cover FOUR of the nine types, with the
 * other five (🎉 👋 💪 ⏳ 🤝) declared as a gap and pinned by test. CD-ICONS-02
 * picked all five — `notif.kudo` · `notif.joined` · `notif.motivate` ·
 * `notif.waiting` · `notif.invite` — so every notification type now resolves to
 * ratified art and the type is no longer `Partial`.
 *
 * ⛔ `plan_escalated` (⏳) is still NOT `sys.clock`. A2 settled the question the
 * free-wins lane raised by picking `notif.waiting` (an hourglass) rather than
 * ruling that the clock serves. An hourglass is not a clock, and now it does
 * not have to be.
 */
export const NOTIFICATION_ICON: Record<NotificationType, IconName> = {
  kudo_received: 'notif.kudo',
  plan_approved: 'sys.check-circle',
  team_joined: 'notif.joined',
  ai_escalation: 'sys.warn',
  motivational_message: 'notif.motivate',
  plan_nudge: 'sys.calendar',
  approval_request: 'nav.notify',
  plan_escalated: 'notif.waiting',
  relationship_invite: 'notif.invite',
  under13_deletion_requested: 'nav.notify',
  // B394 — existing ratified art only: the check for good news, the bell for the rest.
  guardian_license_started: 'sys.check-circle',
  guardian_license_stopped: 'nav.notify',
  guardian_license_stop_failed: 'nav.notify',
  guardian_license_rejected: 'nav.notify',
  guardian_license_ended: 'nav.notify',
};

/**
 * NU-1 (D5) — validate an untrusted `metadata.href` for `plan_nudge`
 * rows. The server stores notification metadata unsanitized, so this
 * frontend allowlist is the ONLY guard between a stored href and
 * `navigate()`. Strict allowlist of basename-relative internal routes;
 * everything else (external URLs, `//host`, `javascript:`, a
 * double-basename `/v2/...`, extra query params) returns `null` and the
 * caller falls back to `/plan`.
 *
 * Consulted for `plan_nudge` and `relationship_invite` rows ONLY — the
 * locked 5 types keep their hardcoded destinations.
 */
const INTERNAL_HREF_ALLOWLIST: readonly RegExp[] = [
  /^\/today$/,
  /^\/plan$/,
  /^\/plan\/week\/\d{1,3}$/,
  /^\/chat\?draftWeek=\d{1,3}$/,
  // Push Lane 2 (push-notifications-v2.md §2.3) — the guardian-approval push
  // deep link lands on the approvals inbox. Mirrored in public/sw.js; the
  // sw-allowlist parity test pins the two copies together.
  /^\/approvals$/,
  // H③a proactive recovery nudge → the recovery coach (Onai) chat, which
  // carries the H② health context. Only this one coach route is emitted.
  /^\/chat\?coach=recovery-nutrition$/,
  // F-514 — relationship_invite deep links: the invited ADULT's accept
  // surface is guardian home; the invited ATHLETE's is Settings → Guardians.
  /^\/guardian$/,
  /^\/settings#guardians$/,
  // Post-run reaction deep link (founder-ruled 2026-08-10). The push used to
  // land on /today and leave the athlete to find the note and tap it — a dead
  // step, and one that broke outright when the note yielded its slot. The tap
  // now opens the run-aware conversation directly; `PushTapMount` mints the
  // §2.1 activation from the `run=` it carries.
  //
  // NOT mirrored in public/sw.js, deliberately: post_run_reaction is an APNs
  // category dispatched only to native devices, so the web service worker can
  // never receive it (same precedent as the three entries above, which are
  // in-app notification rows). Were one ever to reach sw.js it fails safe to
  // the /plan fallback.
  // The uuid shape mirrors the server's own RUN_ID_RX (api/index.js) so a
  // malformed id fails HERE, to the safe /plan fallback, instead of routing to
  // a chat that then dead-ends on the §9.3 "can't load that run" line.
  /^\/chat\?coach=training-coach&run=[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  // C36 — the staleness guard's LOCAL notifications (`lib/run/raceGuardNotify.ts`)
  // carry the run screen, because that is where the R64 sheet renders. NOT
  // mirrored in public/sw.js, deliberately and for the same reason as the entry
  // above: a local notification is scheduled by the native shell and can never
  // reach the web service worker. Both halves stay dark behind
  // `race_stale_guard_v1`.
  /^\/run$/,
];

export function sanitizeInternalHref(href: unknown): string | null {
  if (typeof href !== 'string' || href.length === 0 || href.length > 100) return null;
  return INTERNAL_HREF_ALLOWLIST.some((rx) => rx.test(href)) ? href : null;
}

/**
 * Resolve the React click destination for an allowlisted notification.
 *
 * For 4 of the 5 keys this is a simple route navigate. For
 * `ai_escalation`, the canonical destination is an in-place
 * `<EscalationSummaryModal>` (deferred to PR 2b). Until then, this
 * function returns the secondary CTA target `/v2/chat` so the click
 * still does something useful.
 *
 * @returns the React path to navigate to, OR `null` if the type is
 *          not allowlisted (caller should not surface the row).
 */
export function getNotificationClickPath(type: string): string | null {
  if (!isAllowlistedNotificationType(type)) return null;
  switch (type) {
    case 'kudo_received':
      return '/team/kudos?filter=received';
    case 'plan_approved':
      return '/plan';
    case 'team_joined':
      return '/team';
    case 'ai_escalation':
      // PR 2b: returns null so callers know to render the in-place
      // <EscalationSummaryModal> instead of navigating. The modal's
      // secondary "Talk to coach ›" CTA still routes to /v2/chat
      // when the user opts in — but the default click opens the
      // modal in place, preserving the caller-page context.
      return null;
    case 'motivational_message':
      return '/today';
    case 'plan_nudge':
      // Metadata-less fallback. NotifRow prefers the row's
      // sanitizeInternalHref(metadata.href) deep link when present.
      return '/plan';
    case 'approval_request':
      // CG-4 — cold-open lands on the inbox (design A2).
      return '/approvals';
    case 'plan_escalated':
      // Pass 3 — the coach's review ask lands on the same inbox.
      return '/approvals';
    case 'relationship_invite':
      // F-514 — metadata-less fallback (pre-F-514 rows were adult-initiated,
      // whose recipient is the invited ATHLETE: their accept row lives in
      // Settings → Guardians). NotifRow prefers the row's sanitized
      // metadata.href, which the server now sets per direction.
      return '/settings#guardians';
    case 'under13_deletion_requested':
      // B195 — the pending row with its one-press cancel lives in Settings →
      // Guardians (athlete side). No metadata deep link; one destination.
      return '/settings#guardians';
    case 'guardian_license_started':
    case 'guardian_license_stopped':
    case 'guardian_license_stop_failed':
    case 'guardian_license_rejected':
    case 'guardian_license_ended':
      // B394 — one destination: Settings → Subscription, where the payer covers,
      // manages or stops the plan. The server's metadata.href is the same place
      // (/v2/settings#subscription) but is never consulted, so a stored href
      // cannot steer the click.
      return '/settings#subscription';
    default:
      // Exhaustiveness check — if the allowlist grows without an
      // update to this switch, TS errors here because `type` is no
      // longer narrowed to `never`.
      ((_: never) => _)(type);
      return null;
  }
}

/**
 * Time-ago helper. Returns one of:
 *   "just now" (< 1 min)
 *   "Nm ago"  (< 60 min)
 *   "Nh ago"  (< 24 hrs)
 *   "Nd ago"  (≥ 24 hrs)
 *
 * Locked copy strings — port verbatim from vanilla `notifications.js:9-19`.
 */
export function notificationTimeAgo(timestamp: string | null | undefined): string {
  if (!timestamp) return '';
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
